import { useState } from "react";
import gql from "graphql-tag";

import { Background } from "components/Background/Backgound";
import { NextPageButton } from "components/Button/NextPageButton";
import { PaginatedQuery } from "components/Query/PaginatedQuery";
import { FieldHeader, SortStatus, Table } from "components/Table/Table";
import {
  FeedbackReportSortFieldTypeKnownValue,
  GetFeedbackReports,
} from "generated/copilot-api-developer";
import { useDebounce } from "hooks/useDebounce";
import { useTranslation } from "i18n";

import { feedbackReportRoute } from "./feedbackReportsRoutes";
import {
  FeedbackReportsFilters,
  FeedbackReportsSearchBar,
} from "./FeedbackReportsSearchBar";
import { Rating } from "./Rating";
import { StatusBadge } from "./StatusBadge";

gql`
  # schema = COPILOT_API_DEVELOPER
  query GetFeedbackReports(
    $filters: FeedbackReportsFilter
    $sortFields: [FeedbackReportSortField!]
    $cursor: String
  ) {
    feedbackReports(
      page: { numberOfItems: 20, cursor: $cursor }
      filters: $filters
      sortFields: $sortFields
    ) {
      nextCursor
      hasMore
      data {
        ...FeedbackReport
      }
    }
  }

  fragment FeedbackReport on FeedbackReport {
    uuid
    feedbackId
    comment
    status
    user {
      externalId
    }
    createdAt
    updatedAt
    endpoint
    version
    rating
    replyTo
    specialty
    locale
    templateKey
  }
`;

export const FeedbackReportsList = () => {
  const t = useTranslation();
  const [filters, setFilters] = useState<FeedbackReportsFilters>({
    from: undefined,
    to: undefined,
    feedbackId: "",
    userExternalId: "",
    statusFilter: undefined,
  });
  const [sortStatus, setSortStatus] = useState<
    SortStatus<FeedbackReportSortFieldTypeKnownValue>
  >({ id: "CREATED_AT", isAscending: false });
  const { from, to, feedbackId, userExternalId, statusFilter } = filters;
  const debouncedFeedbackId = useDebounce(feedbackId, 300);
  const debouncedUserExternalId = useDebounce(userExternalId, 300);

  const invalidDateRange =
    from !== undefined && to !== undefined && from.isAfter(to);

  const sortableHeader = (
    id: FeedbackReportSortFieldTypeKnownValue,
    name: string,
  ): FieldHeader<FeedbackReportSortFieldTypeKnownValue> => ({
    id,
    name,
    sortable: true,
  });

  return (
    <Background className="flex-col flex-fill overflow-auto p-16 lg:p-44 space-y-24">
      <div className="flex-col">
        <h1 className="text-primary-dark text-24 font-bold">
          {t("developers.feedback_reports.feedback_reports")}
        </h1>
        <p>{t("developers.feedback_reports.description")}</p>
      </div>
      <FeedbackReportsSearchBar
        filters={filters}
        onChange={setFilters}
        invalidDateRange={invalidDateRange}
      />
      <PaginatedQuery
        query={GetFeedbackReports}
        variables={{
          filters: {
            createdAtAfter: from,
            createdAtBefore: to,
            feedbackId: debouncedFeedbackId ? debouncedFeedbackId : undefined,
            userExternalId: debouncedUserExternalId
              ? debouncedUserExternalId
              : undefined,
            statuses: statusFilter?.statuses,
          },
          sortFields: [
            {
              fieldType: sortStatus.id,
              order: sortStatus.isAscending ? "ASC" : "DESC",
            },
          ],
        }}
        pollInterval={60_000}
      >
        {({ data }, utils) => (
          <>
            <Table
              elements={data}
              doRowAction={(report) => ({
                mode: "navigate",
                to: feedbackReportRoute(report.uuid),
              })}
              fieldHeaders={[
                sortableHeader(
                  "FEEDBACK_ID",
                  t("developers.feedback_reports.feedback_id"),
                ),
                sortableHeader(
                  "CREATED_AT",
                  t("developers.feedback_reports.created_at"),
                ),
                sortableHeader(
                  "USER_EXTERNAL_ID",
                  t("developers.feedback_reports.external_user_id"),
                ),
                t("developers.feedback_reports.comment"),
                sortableHeader(
                  "RATING",
                  t("developers.feedback_reports.rating"),
                ),
                sortableHeader(
                  "STATUS",
                  t("developers.feedback_reports.status"),
                ),
              ]}
              sortStatus={sortStatus}
              onClickSortableHeader={(id) =>
                setSortStatus((previous) => ({
                  id,
                  isAscending:
                    previous.id === id ? !previous.isAscending : true,
                }))
              }
              fields={(report) => [
                <div
                  className="truncate max-w-[200px]"
                  title={report.feedbackId ?? undefined}
                  key="id"
                >
                  {report.feedbackId}
                </div>,
                <div className="whitespace-nowrap" key="date">
                  {report.createdAt.format("dateAndTime")}
                </div>,
                <div
                  className="max-w-[160px] line-clamp-2 break-words"
                  title={report.user?.externalId ?? undefined}
                  key="user"
                >
                  {report.user?.externalId}
                </div>,
                <div
                  className="min-w-[300px] max-w-[520px] line-clamp-2 whitespace-pre-wrap break-words"
                  title={report.comment ?? undefined}
                  key="comment"
                >
                  {report.comment}
                </div>,
                <Rating rating={report.rating} key="rating" />,
                <StatusBadge status={report.status} key="st" />,
              ]}
            />
            <NextPageButton {...utils} />
          </>
        )}
      </PaginatedQuery>
    </Background>
  );
};
